import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { FaCircleMinus } from 'react-icons/fa6';
import * as watchlist from '../api/watchlist';
import { Link, Redirect } from 'react-router-dom';
// Watchlist data is now subscribed once in AuthContext and provided as context

export default function WatchlistPage() {
  const { user, loading, watchlist: authWatchlist } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const checkedRef = useRef(new Set());

  // Reflect context watchlist into local UI state
  useEffect(() => {
    setItems(Array.isArray(authWatchlist) ? authWatchlist : []);
    checkedRef.current.clear();
  }, [authWatchlist]);

  // Cleanup and enrichment: remove entries whose popup no longer exists,
  // and backfill missing thumbnail/title/link from popup detail to fix broken tiles.
  useEffect(() => {
    if (!user || !items?.length) return;
    const toCheck = items.filter(it => !checkedRef.current.has(String(it.popupId)));
    if (!toCheck.length) return;
    const run = async () => {
      for (const it of toCheck) {
        const pid = String(it.popupId);
        checkedRef.current.add(pid);
        try {
          const r = await fetch(`/api/popups/${encodeURIComponent(pid)}`, { cache: 'no-store' });
          if (!r.ok) {
            // popup no longer exists -> remove from watchlist
            try { await watchlist.remove(user.uid, pid); } catch {}
            continue;
          }
          const popup = await r.json();
          // Prefer tile miniatura (listThumb) so Watchlist mirrors catalog tiles.
          const bestImg = popup.listThumb || it.img || '';
          const bestTitle = popup.title || it.title || '';
          const bestLink = `/?preview=${encodeURIComponent(pid)}`;
          // overwrite entry if we improved anything
          if (bestImg !== it.img || bestTitle !== it.title || bestLink !== it.link) {
            try {
              await watchlist.add(user.uid, { popupId: pid, title: bestTitle, img: bestImg, link: bestLink });
            } catch {}
          }
        } catch (e) {
          // network error ignored
        }
      }
    };
    run();
  }, [user?.uid, items]);

  // React to Admin catalog/search changes to update Watchlist immediately
  useEffect(() => {
    if (!user) return;
    const onChanged = async (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : {};
        const pid = detail && detail.popupId != null ? String(detail.popupId) : null;
        if (!pid) return;
        // fetch latest popup to compute preferred fields
        let popup = null;
        try {
          const r = await fetch(`/api/popups/${encodeURIComponent(pid)}`, { cache: 'no-store' });
          if (r.ok) popup = await r.json();
        } catch {}
        if (!popup) return;
        // Always use the tile miniatura for Watchlist (listThumb). If missing, keep previous image.
        const bestImg = popup.listThumb || '';
        const bestTitle = popup.title || '';
        const bestLink = `/?preview=${encodeURIComponent(pid)}`;

        // update local UI state if present
        setItems((prev) => (prev || []).map((it) => (
          String(it.popupId) === pid ? { ...it, img: bestImg, title: bestTitle, link: bestLink } : it
        )));

        // persist into Firestore so snapshot reflects new fields next time
        try { await watchlist.add(user.uid, { popupId: pid, title: bestTitle, img: bestImg || undefined, link: bestLink }); } catch {}
      } catch {}
    };
    window.addEventListener('catalog:changed', onChanged);
    window.addEventListener('search:changed', onChanged);
    return () => {
      window.removeEventListener('catalog:changed', onChanged);
      window.removeEventListener('search:changed', onChanged);
    };
  }, [user?.uid]);

  if (loading) return <Container><Title>Načítám…</Title></Container>;
  if (!user) return <Redirect to="/" />;

  const onRemove = async (popupId) => {
    if (!user || busy) return;
    setBusy(true);
    try { await watchlist.remove(user.uid, popupId); } finally { setBusy(false); }
  };

  return (
    <Container>
      <h4 className="serialy">Watchlist</h4>
      {items.length === 0 ? (
        <Empty>Tvůj watchlist je prázdný.</Empty>
      ) : (
        <Grid>
          {items.map((it) => (
            <Card key={it.id}>
              <ThumbWrap>
                {it.link && it.link.includes('preview=') ? (
                  <div
                    className="prevent-select"
                    role="button"
                    tabIndex={0}
                    aria-label={it.title || 'item'}
                    onClick={(ev) => {
                      try {
                        const url = new URL(it.link, window.location.href);
                        window.history.pushState({}, '', url.pathname + url.search);
                        const id = url.searchParams.get('preview');
                        window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } }));
                      } catch (_) {}
                    }}
                  >
                    <img src={it.img || '/images/placeholder-thumb.png'} alt={it.title || ''} />
                  </div>
                ) : (
                  <Link to={it.link || `/?preview=${encodeURIComponent(it.popupId)}`} aria-label={it.title || 'item'}>
                    <img src={it.img || '/images/placeholder-thumb.png'} alt={it.title || ''} />
                  </Link>
                )}
                <RemoveBtn onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(it.popupId); }} title="Odebrat z watchlistu" aria-label="Odebrat z watchlistu">
                  <FaCircleMinus color="#fff" size={18} />
                </RemoveBtn>
              </ThumbWrap>
            </Card>
          ))}
        </Grid>
      )}
    </Container>
  );
}

const Container = styled.div`
  font-size: 25px;
  padding: 0 42px;
  color: #fff;

  h4 {
    font-family: 'Noto Sans Lao', sans-serif;
    margin-bottom: 10px;
  }
`;
const Empty = styled.div`
  opacity: .8;
`;
const Grid = styled.div`
  display: grid;
  grid-gap: 20px;
  grid-template-columns: repeat(5, minmax(180px, 1fr));
  @media (max-width: 1400px) { grid-template-columns: repeat(4, minmax(160px, 1fr)); }
  @media (max-width: 1100px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: 800px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 420px) { grid-template-columns: repeat(1, minmax(0, 1fr)); }
`;
const Card = styled.div`
  border-radius: 4px;
  box-shadow: rgb(0 0 0 / 69%) 0px 26px 30px -10px,
    rgb(0 0 0 / 73%) 0px 16px 10px -10px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s;
  outline: 0px solid transparent;
  background: linear-gradient(rgb(48, 50, 62), rgb(30, 31, 42));
  transform-origin: center center;
  will-change: transform, box-shadow;

  &:hover {
    outline: 3.2px solid #C7C7C7;
    box-shadow: rgb(0 0 0 / 90%) 0px 42px 64px -18px, rgb(0 0 0 / 76%) 0px 34px 24px -12px;
    transform: scale(1.035);
    transition: all 0.3s ease-in-out;
    -webkit-transition: all 0.3s ease-in-out;
  }
`;
const ThumbWrap = styled.div`
  position: relative;
  border-radius: 4px;
  padding-top: 56.25%;
  box-shadow: rgb(0 0 0 / 69%) 0px 26px 30px -10px,
    rgb(0 0 0 / 73%) 0px 16px 10px -10px;
  overflow: hidden;
  background: linear-gradient(rgb(48, 50, 62), rgb(30, 31, 42));
  transform-origin: center center;
  will-change: transform, box-shadow;

  img {
    position:absolute; inset:0; width:100%; height:100%; object-fit:cover;
    display:block; opacity:1; z-index:1; top:0;
    -webkit-backface-visibility: visible; backface-visibility: visible;
    transform: none !important;
    transition: filter 180ms ease, opacity 180ms ease;
  }

  &:hover {
    outline: 3.2px solid #C7C7C7;
    box-shadow: rgb(0 0 0 / 90%) 0px 42px 64px -18px, rgb(0 0 0 / 76%) 0px 34px 24px -12px;
    transform: scale(1.035);
    transition: all 0.3s ease-in-out;
    -webkit-transition: all 0.3s ease-in-out;
  }
`;
const RemoveBtn = styled.button`
  position: absolute;
  top: 8px; right: 8px;
  width: 28px; height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(0,0,0,0.5);
  display: flex; align-items:center; justify-content:center;
  cursor: pointer;
`;
// no caption under tiles
